import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Download, Eye, Upload, X } from "lucide-react";

import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { EmptyTable } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { NoTranslate } from "@/core/components/ui/no-translate";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/core/components/ui/table";
import { useFileUpload } from "@/core/hooks/use-file-upload";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { formatDateTime } from "@/core/utils/func/format-date";
import { KYC_FILE_ACCEPT } from "@/core/utils/func/input-normalization";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { notifSilence, notifSilenceError, notifSilenceWarning } from "@/core/utils/func/notifApp";
import type { KycDocumentDto } from "@/features/kyc/api/dto";
import { useDocumentDownloadUrl } from "@/features/kyc/api/use-document-download-url";
import { useReviewKycDocument } from "@/features/kyc/api/use-review-kyc-document";
import { useUploadKycDocument } from "@/features/kyc/api/use-upload-kyc-document";
import { KycDocumentPreviewDialog } from "@/features/kyc/components/kyc-documents/kyc-document-preview-dialog";
import { KycDocumentRejectDialog } from "@/features/kyc/components/kyc-documents/kyc-document-reject-dialog";
import { createKycDocumentUploadSchema } from "@/features/kyc/schemas/kyc-document.schema";
import {
  KYC_DOCUMENT_TYPES,
  UPLOADED_VIA,
  type KycDocumentStatus,
  type KycDocumentType,
  type KycCaseStatus,
  type UploadedVia,
} from "@/features/shared/api/enums";

/** Taille maximale d'une pièce : 10 Mio. */
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const STATUS_VARIANTS: Record<
  KycDocumentStatus,
  "warning" | "success" | "destructive"
> = {
  PENDING: "warning",
  VALIDATED: "success",
  REJECTED: "destructive",
};

/**
 * Pièces justificatives d'un dossier KYC (guide §6.2) : liste des pièces
 * (type, statut, motif de rejet, date), prévisualisation (dialog image/PDF)
 * et téléchargement via URL présignée GET (rôles de revue), actions de
 * revue Valider/Rejeter sur les pièces PENDING (motif obligatoire au rejet)
 * et zone d'upload tant que le dossier est en DRAFT (presign → PUT direct
 * stockage → confirm — le binaire ne traverse jamais l'API).
 *
 * Consommé par la page détail dossier avec EXACTEMENT ces props — contrat
 * figé, ne pas modifier la signature.
 */
export function KycCaseDocuments({
  kycCaseId,
  documents,
  caseStatus,
}: {
  kycCaseId: string;
  documents: KycDocumentDto[];
  caseStatus: KycCaseStatus;
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  // Les clés kyc.documents.* sont fusionnées dans src/locales — le cast
  // reste nécessaire tant que le type TFunctionType n'est pas regénéré.
  const tDoc = (key: string) => t(key as TFunctionType);

  const uploadSchema = useMemo(
    () => createKycDocumentUploadSchema(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue (schéma zod traduit reconstruit au changement de langue).
    [i18n.language]
  );
  const uploadMessages = useMemo(
    () => ({
      fileTooLarge: (fileName: string, maxSize: string) =>
        tDoc("kyc.documents.upload.errors.fileTooLarge")
          .replace("{fileName}", fileName)
          .replace("{maxSize}", maxSize),
      fileTypeNotAccepted: (fileName: string) =>
        tDoc("kyc.documents.upload.errors.fileTypeNotAccepted").replace(
          "{fileName}",
          fileName
        ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  );

  const downloadUrl = useDocumentDownloadUrl();
  const previewUrl = useDocumentDownloadUrl();
  const review = useReviewKycDocument();
  const upload = useUploadKycDocument();

  const [rejectTarget, setRejectTarget] = useState<KycDocumentDto | undefined>();
  const [validateTarget, setValidateTarget] = useState<KycDocumentDto | undefined>();
  const [previewTarget, setPreviewTarget] = useState<KycDocumentDto | undefined>();
  const [previewDownloadUrl, setPreviewDownloadUrl] = useState<string | null>(null);
  const [uploadPreviewOpen, setUploadPreviewOpen] = useState(false);
  const [documentType, setDocumentType] = useState<KycDocumentType>("NATIONAL_ID");
  const [uploadedVia, setUploadedVia] = useState<UploadedVia>("AGENT");
  const [confirmOnly, setConfirmOnly] = useState(false);
  const [storageBucket, setStorageBucket] = useState("");
  const [storageKey, setStorageKey] = useState("");

  const [{ files, errors: uploadErrors }, { getInputProps, clearFiles }] =
    useFileUpload({
      accept: KYC_FILE_ACCEPT,
      maxSize: MAX_FILE_SIZE,
      messages: uploadMessages,
    });

  const handleDownload = (documentId: string) => {
    downloadUrl.mutate(
      { kycCaseId, documentId },
      {
        onSuccess: (data) => {
          window.open(data.downloadUrl, "_blank", "noopener,noreferrer");
        },
        onError: (error) => notifSilenceError(error.message),
      }
    );
  };

  const fetchPreviewUrl = (doc: KycDocumentDto) => {
    setPreviewDownloadUrl(null);
    previewUrl.mutate(
      { kycCaseId, documentId: doc.id },
      {
        onSuccess: (data, variables) => {
          if (variables.documentId !== doc.id) return;
          setPreviewDownloadUrl(data.downloadUrl);
        },
        onError: (error) => notifSilenceError(error.message),
      }
    );
  };

  const handlePreview = (doc: KycDocumentDto) => {
    setPreviewTarget(doc);
    fetchPreviewUrl(doc);
  };

  const handleValidate = (documentId: string) => {
    review.mutate(
      { kycCaseId, documentId, decision: "VALIDATED" },
      {
        onSuccess: () => notifSilence(tDoc("kyc.documents.feedback.validated")),
        onError: (error) => notifSilenceError(error.message),
      }
    );
  };

  const handleUpload = () => {
    const parsed = uploadSchema.safeParse({ documentType, uploadedVia });
    if (!parsed.success) return;
    const entry = files[0];
    if (!entry || !(entry.file instanceof File)) {
      notifSilenceError(tDoc("kyc.documents.upload.errors.fileRequired"));
      return;
    }
    upload.mutate(
      {
        kycCaseId,
        file: entry.file,
        documentType: parsed.data.documentType,
        uploadedVia: parsed.data.uploadedVia,
        ...(confirmOnly
          ? {
              storageOverride: {
                storageBucket: storageBucket.trim(),
                storageKey: storageKey.trim(),
              },
            }
          : {}),
      },
      {
        onSuccess: () => {
          notifSilence(tDoc("kyc.documents.feedback.uploaded"));
          clearFiles();
          setConfirmOnly(false);
          setStorageBucket("");
          setStorageKey("");
        },
        onError: (error) => {
          if (error instanceof Error && error.message === "PRESIGN_UNAVAILABLE") {
            setConfirmOnly(true);
            notifSilenceWarning(tDoc("kyc.documents.presignUnavailable"));
            return;
          }
          notifSilenceError(apiErrorHandler(error));
        },
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {tDoc("kyc.documents.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{tDoc("kyc.documents.columns.documentType")}</TableHead>
              <TableHead>{tDoc("kyc.documents.columns.status")}</TableHead>
              <TableHead>{tDoc("kyc.documents.columns.rejectionReason")}</TableHead>
              <TableHead>{tDoc("kyc.documents.columns.uploadedVia")}</TableHead>
              <TableHead>{tDoc("kyc.documents.columns.uploadedAt")}</TableHead>
              <TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.length === 0 ? (
              <EmptyTable />
            ) : (
              documents.map((doc) => {
                const uploadedAt = formatDateTime(doc.uploadedAt) as unknown as
                  | string
                  | undefined;
                const downloading =
                  downloadUrl.isPending &&
                  downloadUrl.variables?.documentId === doc.id;
                const previewing =
                  previewUrl.isPending &&
                  previewUrl.variables?.documentId === doc.id;
                const reviewing =
                  review.isPending && review.variables?.documentId === doc.id;
                return (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <NoTranslate as="span" className="font-medium">
                        {doc.documentType}
                      </NoTranslate>
                    </TableCell>
                    <TableCell>
                      <NoTranslate as="span">
                        <Badge variant={STATUS_VARIANTS[doc.status] ?? "warning"}>
                          {doc.status}
                        </Badge>
                      </NoTranslate>
                    </TableCell>
                    <TableCell>
                      <NoTranslate as="span">
                        {doc.rejectionReason || "—"}
                      </NoTranslate>
                    </TableCell>
                    <TableCell>
                      <NoTranslate as="span">{doc.uploadedVia}</NoTranslate>
                    </TableCell>
                    <TableCell>
                      <NoTranslate as="span">{uploadedAt ?? "—"}</NoTranslate>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Téléchargement et revue : rôles de revue uniquement
                            (guide §6.2 — la fuite d'une URL signée équivaut à
                            la fuite du document d'identité). */}
                        <RequireRole
                          roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            title={tDoc("kyc.documents.actions.preview")}
                            disabled={previewing}
                            onClick={() => handlePreview(doc)}
                          >
                            {previewing ? (
                              <ButtonSpinner size="sm" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title={tDoc("kyc.documents.actions.download")}
                            disabled={downloading}
                            onClick={() => handleDownload(doc.id)}
                          >
                            {downloading ? (
                              <ButtonSpinner size="sm" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </Button>
                        </RequireRole>
                        {doc.status === "PENDING" ? (
                          <RequireRole
                            roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              title={tDoc("kyc.documents.actions.validate")}
                              disabled={reviewing}
                              onClick={() => setValidateTarget(doc)}
                            >
                              {reviewing ? (
                                <ButtonSpinner size="sm" />
                              ) : (
                                <Check className="h-4 w-4 text-success" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title={tDoc("kyc.documents.actions.reject")}
                              disabled={reviewing}
                              onClick={() => setRejectTarget(doc)}
                            >
                              <X className="h-4 w-4 text-destructive" />
                            </Button>
                          </RequireRole>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {caseStatus === "DRAFT" ? (
          <div className="flex flex-col gap-3 rounded-md border border-dashed border-border p-4">
            <p className="text-sm font-medium text-foreground">
              {tDoc("kyc.documents.upload.title")}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5">
                <Label>{tDoc("kyc.documents.upload.documentType")}</Label>
                <Select
                  value={documentType}
                  onValueChange={(v) => setDocumentType(v as KycDocumentType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KYC_DOCUMENT_TYPES.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{tDoc("kyc.documents.upload.uploadedVia")}</Label>
                <Select
                  value={uploadedVia}
                  onValueChange={(v) => setUploadedVia(v as UploadedVia)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UPLOADED_VIA.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 lg:col-span-2">
                <Label>{tDoc("kyc.documents.upload.file")}</Label>
                <Input {...getInputProps()} disabled={upload.isPending} />
              </div>
            </div>
            {confirmOnly ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <p className="text-sm text-warning sm:col-span-2">
                  {tDoc("kyc.documents.confirmOnly.hint")}
                </p>
                <div className="space-y-1.5">
                  <Label htmlFor="storage-bucket">
                    {tDoc("kyc.documents.confirmOnly.storageBucket")}
                  </Label>
                  <Input
                    id="storage-bucket"
                    className="notranslate"
                    value={storageBucket}
                    placeholder={tDoc(
                      "kyc.documents.confirmOnly.storageBucketPlaceholder"
                    )}
                    onChange={(e) => setStorageBucket(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="storage-key">
                    {tDoc("kyc.documents.confirmOnly.storageKey")}
                  </Label>
                  <Input
                    id="storage-key"
                    className="notranslate"
                    value={storageKey}
                    placeholder={tDoc(
                      "kyc.documents.confirmOnly.storageKeyPlaceholder"
                    )}
                    onChange={(e) => setStorageKey(e.target.value)}
                  />
                </div>
              </div>
            ) : null}
            {uploadErrors.length > 0 ? (
              <NoTranslate as="div" className="text-sm text-destructive">
                {uploadErrors.map((error) => (
                  <p key={error}>{error}</p>
                ))}
              </NoTranslate>
            ) : null}
            <div>
              <Button
                onClick={() => setUploadPreviewOpen(true)}
                disabled={
                  upload.isPending ||
                  (confirmOnly &&
                    (!storageBucket.trim() || !storageKey.trim()))
                }
              >
                {upload.isPending ? (
                  <ButtonSpinner />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                {tDoc("kyc.documents.upload.submit")}
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>

      <KycDocumentPreviewDialog
        open={Boolean(previewTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewTarget(undefined);
            setPreviewDownloadUrl(null);
          }
        }}
        document={previewTarget}
        downloadUrl={previewDownloadUrl}
        loading={
          previewUrl.isPending &&
          previewUrl.variables?.documentId === previewTarget?.id
        }
        onRetry={previewTarget ? () => fetchPreviewUrl(previewTarget) : undefined}
      />
      <KycDocumentRejectDialog
        open={Boolean(rejectTarget)}
        onOpenChange={(open) => {
          if (!open) setRejectTarget(undefined);
        }}
        kycCaseId={kycCaseId}
        document={rejectTarget}
      />
      <MutationConfirmPreview
        open={Boolean(validateTarget)}
        onOpenChange={(open) => {
          if (!open) setValidateTarget(undefined);
        }}
        data={{
          id: validateTarget?.id,
          documentType: validateTarget?.documentType,
          status: validateTarget?.status,
          decision: "VALIDATED",
        }}
        title={t("common.mutationPreview.title")}
        description={t("common.mutationPreview.description")}
        t={tDoc}
        pending={review.isPending}
        onConfirm={() => {
          if (!validateTarget?.id) return;
          handleValidate(validateTarget.id);
          setValidateTarget(undefined);
        }}
      />
      <MutationConfirmPreview
        open={uploadPreviewOpen}
        onOpenChange={setUploadPreviewOpen}
        data={{
          kycCaseId,
          documentType,
          uploadedVia,
          fileName: files[0]?.file instanceof File ? files[0].file.name : undefined,
          confirmOnly,
        }}
        title={t("common.mutationPreview.createTitle")}
        description={t("common.mutationPreview.createDescription")}
        t={tDoc}
        pending={upload.isPending}
        onConfirm={() => {
          handleUpload();
          setUploadPreviewOpen(false);
        }}
      />
    </Card>
  );
}
